import couponPicture from '@/assets/coupon@2x.png'
import { isCouponApiSuccess } from './couponApi'
import { formatCouponPicturePathForApi } from './CouponCreate'
import {
  postCouponDetail,
  resolveCouponPictureUrl,
  type CouponDetailData,
} from './CouponDetai'

export interface CreateCouponInitialValues {
  name?: string
  code: string
  discountType: string
  discountAmount: string
  minSpend: string
  minSpendNotRequired: boolean
  startType: string
  startTime: string
  endType: string
  endTime: string
  eligibleServices: string[]
  usageLimit: string
  eligibleRegions: string
  maxRedemptions: string
  unlimitedRedemptions: boolean
  description: string
  picturePreview?: string
  /** 后端可存的相对 path；复制提交时原样带回 */
  picturePath?: string
}

function parseNumericAmount(value: string): string {
  const normalized = value.replace(/[^0-9.]/g, '')
  return normalized || value.trim()
}

function mapDiscountTypeFromApi(type?: string): string {
  const normalized = (type ?? '').toLowerCase()
  if (normalized.includes('percent')) return 'percentage_off'
  return 'fixed_amount'
}

function mapDiscountAmountFromApi(data: CouponDetailData): string {
  if (typeof data.discount_value === 'number') {
    return String(data.discount_value)
  }
  if (data.discount_text?.trim()) {
    return parseNumericAmount(data.discount_text)
  }
  return ''
}

function mapMinSpendFromApi(data: CouponDetailData): {
  minSpend: string
  minSpendNotRequired: boolean
} {
  const minSpendText = (data.min_spend_text ?? '').toLowerCase()
  const notRequired =
    data.min_spend_required === 0 ||
    minSpendText.includes('not required') ||
    minSpendText === ''

  if (notRequired) {
    return { minSpend: '', minSpendNotRequired: true }
  }

  if (typeof data.min_spend_amount === 'number') {
    return { minSpend: String(data.min_spend_amount), minSpendNotRequired: false }
  }

  return {
    minSpend: parseNumericAmount(data.min_spend_text ?? ''),
    minSpendNotRequired: false,
  }
}

function parseApiDateTimeToIso(value?: string): string {
  const raw = (value ?? '').trim()
  if (!raw) return ''

  const normalized = raw.includes('T') ? raw : raw.replace(' ', 'T')
  const date = new Date(normalized)
  if (Number.isNaN(date.getTime())) return raw

  const pad = (part: number) => String(part).padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`
}

function mapEndTypeFromApi(endMode?: string, validityText?: string): string {
  const mode = (endMode ?? '').toLowerCase()
  if (mode.includes('never')) return 'never_expires'
  if ((validityText ?? '').toLowerCase().includes('never')) return 'never_expires'
  return 'specific_time'
}

function mapEligibleServicesFromApi(services?: string[]): string[] {
  if (!Array.isArray(services) || services.length === 0) return ['all_services']
  return services.map((service) => {
    const normalized = service.trim().toLowerCase()
    if (normalized === 'all' || normalized === '2') return 'all_services'
    return service
  })
}

function mapUsageLimitFromApi(type?: string, text?: string): string {
  const normalized = (type ?? text ?? '').toLowerCase()
  if (normalized.includes('one_per_address') && !normalized.includes('account')) {
    return 'one_per_address'
  }
  if (normalized.includes('per address') && !normalized.includes('account')) {
    return 'one_per_address'
  }
  if (
    normalized === 'unlimited' ||
    normalized.includes('no_limit') ||
    normalized.includes('no usage')
  ) {
    return 'no_limit'
  }
  return 'one_per_account'
}

function mapEligibleRegionsFromApi(regions?: string, text?: string): string {
  const normalized = (regions ?? text ?? '').toLowerCase()
  if (!normalized || normalized.includes('unlimited')) return 'unlimited'
  return regions?.trim() || 'unlimited'
}

function mapRedemptionsFromApi(data: CouponDetailData): {
  maxRedemptions: string
  unlimitedRedemptions: boolean
} {
  if (data.total_redemptions_unlimited === 1) {
    return { maxRedemptions: '', unlimitedRedemptions: true }
  }

  const redemptionText = (data.redemptions_text ?? '').toLowerCase()
  if (redemptionText.includes('unlimited')) {
    return { maxRedemptions: '', unlimitedRedemptions: true }
  }

  if (typeof data.total_redemptions_limit === 'number' && data.total_redemptions_limit > 0) {
    return {
      maxRedemptions: String(data.total_redemptions_limit),
      unlimitedRedemptions: false,
    }
  }

  const match = data.redemptions_text?.match(/\/(\d+)/)
  if (match?.[1]) {
    return { maxRedemptions: match[1], unlimitedRedemptions: false }
  }

  return { maxRedemptions: '', unlimitedRedemptions: true }
}

/** Generate a unique coupon code for copy flow. */
export function buildCopyCouponCode(originalCode: string): string {
  const raw = originalCode.trim()
  const baseMatch = raw.match(/^(.*?)(?:_COPY[\w-]*)*$/i)
  const base = (baseMatch?.[1] || raw || 'COUPON').trim() || 'COUPON'
  const stamp = Date.now().toString().slice(-6)
  const rand = Math.random().toString(36).slice(2, 5).toUpperCase()
  const suffix = `_COPY${stamp}${rand}`
  const maxBaseLength = Math.max(1, 50 - suffix.length)
  return `${base.slice(0, maxBaseLength)}${suffix}`
}

function readCouponPictureRaw(data: CouponDetailData): string {
  const keys = [
    'picture_url',
    'picture',
    'image_url',
    'image',
    'photo_url',
    'photo',
    'picture_path',
    'image_path',
  ] as const

  for (const key of keys) {
    const value = data[key]
    if (typeof value === 'string' && value.trim()) return value.trim()
  }
  return ''
}

export function mapCouponDetailToCopyInitialValues(
  data: CouponDetailData,
  fallbackPicture = couponPicture,
): CreateCouponInitialValues {
  const minSpendValues = mapMinSpendFromApi(data)
  const redemptionValues = mapRedemptionsFromApi(data)
  const endType = mapEndTypeFromApi(data.end_mode, data.validity_period_text)
  const pictureRaw = readCouponPictureRaw(data)
  const picturePath = formatCouponPicturePathForApi(pictureRaw)

  return {
    name: (data.name ?? '').trim(),
    code: buildCopyCouponCode(data.code ?? ''),
    discountType: mapDiscountTypeFromApi(data.discount_type),
    discountAmount: mapDiscountAmountFromApi(data),
    minSpend: minSpendValues.minSpend,
    minSpendNotRequired: minSpendValues.minSpendNotRequired,
    startType: data.start_mode?.trim() || 'immediately',
    startTime:
      data.start_mode === 'specific_time' ? parseApiDateTimeToIso(data.start_time) : '',
    endType,
    endTime: endType === 'specific_time' ? parseApiDateTimeToIso(data.end_time) : '',
    eligibleServices: mapEligibleServicesFromApi(data.eligible_services),
    usageLimit: mapUsageLimitFromApi(data.usage_limit_type, data.usage_limit_text),
    eligibleRegions: mapEligibleRegionsFromApi(data.eligible_regions, data.eligible_regions_text),
    maxRedemptions: redemptionValues.maxRedemptions,
    unlimitedRedemptions: redemptionValues.unlimitedRedemptions,
    description: data.description?.trim() ?? '',
    picturePreview: resolveCouponPictureUrl(pictureRaw) || fallbackPicture,
    picturePath,
  }
}

/**
 * 打开复制弹窗：POST /coupon/detail 填表；用户 Submit 再走 POST /coupon/create。
 * 不要调用 /api/admin/coupon/copy。
 */
export async function fetchCouponCopyInitialValues(
  couponId: string,
): Promise<CreateCouponInitialValues> {
  const normalizedId = couponId.trim()
  if (!normalizedId) {
    throw new Error('Coupon ID is required')
  }

  const response = await postCouponDetail({ coupon_id: normalizedId })
  if (isCouponApiSuccess(response.code) && response.data?.coupon_id) {
    return mapCouponDetailToCopyInitialValues(response.data)
  }

  throw new Error(response.msg || 'Failed to load coupon for copy')
}
